import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Plus, Loader2, TrendingUp, X } from 'lucide-react';
import { useAppContext } from '../../../context/AppContext';
import { distributionService } from '../../../services/distributionService';
import MantleDataGrid, { gcell } from '../../../components/MantleDataGrid';
import PickerPopover from '../../../components/PickerPopover';
import SearchSelect from '../../../components/SearchSelect';
import PostingSummary from '../../../components/PostingSummary';
import { PERIODS, periodLabel } from '../../../utils/financialPeriods';
import usePeriodDrilldown, { periodColumns, periodsOfCol, periodHeaderToggle, aggPeriods } from '../../../hooks/usePeriodDrilldown';

// =============================================================================
// Warehousing > Variable Running Costs
//
// Costs that scale with an operational driver: cost = rate x driver quantity,
// per period. The driver quantities live on the Operational Drivers page; the
// expense lines themselves are defined in Budget Setup > Variable Expenses.
// What is entered HERE is the rate, and which of the site's drivers each line
// runs off.
//
// ONE ROW PER EXPENSE. The month columns ARE the rates. Typing in the Rate
// column fills all twelve; overtype any single month afterwards to step a
// tariff up in January or land a wage settlement in July. No expand, no second
// row, no mode to be in — the common case (one rate all year) is one keystroke,
// and the exception is editing the month you mean. There is no effective-date
// model either: over twelve periods, twelve numbers say everything a date range
// would, and every reader can add them up without interpreting a rule.
//
// The months therefore show RATES, not money. Cost per month is real but it is
// output, not input, so it lives in the posting summary at the foot of the page
// — grouped the way it actually hits the ledger. The Annual Cost column carries
// the per-line total so the grid still answers "what does this line cost".
//
// The TOTAL row leaves the month columns blank on purpose. Summing a column of
// rates would add rand-per-pallet to rand-per-square-metre to a percentage of
// stock value; the number would render, and it would mean nothing.
//
// GL comes from the catalogue and is the same at every site. Cost centre and
// driver come from the catalogue too but may be overridden here, because a DC
// is usually its own cost centre and a cold store may bill floor area where
// everyone else bills throughput. A blank override means "inherit", and the
// grid shows the inherited value greyed so the difference is visible.
// =============================================================================

const T_TOTAL = { bgCell: 'rgba(100, 116, 139, 0.22)', baseFontStyle: '600 12px' };
const T_AGG = { bgCell: 'rgba(100, 116, 139, 0.14)', baseFontStyle: '600 12px' };
const T_INHERITED = { textDark: '#94a3b8' };
const T_WARN = { textDark: '#b45309' };
// A month whose rate has been taken off the base rate — the exception the whole
// single-row design exists to make easy to spot at a glance.
const T_OVERRIDDEN = { textDark: '#1d4ed8', baseFontStyle: '600 12px' };

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
// Rates run to fractions of a cent (shrink wrap per case), so they are not
// formatted to two places like money.
const fmtRate = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 6 });

// ---- Escalation dialog -------------------------------------------------------
// The answer to "the rate changes in July". Writes the twelve numbers rather
// than storing a rule, so what is budgeted is exactly what is shown.
const EscalateModal = ({ fyStartMonth, lines, onApply, onClose }) => {
  const [pct, setPct] = useState('6');
  const [from, setFrom] = useState('7');
  // Everything ticked to start: escalating the lot is the common case, and
  // un-ticking the two contract-fixed lines is less work than ticking the
  // other twenty.
  const [picked, setPicked] = useState(() => new Set(lines.map((l) => l.expense_site_id)));

  const toggle = (id) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const all = () => setPicked(new Set(lines.map((l) => l.expense_site_id)));
  const none = () => setPicked(new Set());

  const pctNum = Number(pct);
  const fromNum = Number(from);
  const problem = !Number.isFinite(pctNum) ? 'Enter a percentage.'
    : !(fromNum >= 2 && fromNum <= 12) ? 'Pick a period from 2 to 12.'
    : picked.size === 0 ? 'Choose at least one line.' : '';

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-6"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/95 flex items-start justify-between gap-3">
          <div>
            <h3 className="m-0 text-sm font-bold text-slate-800 dark:text-slate-100">Escalate rates</h3>
            <p className="m-0 mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              Steps every rate up from a chosen month — a tariff increase, a union settlement, an indexed contract.
            </p>
          </div>
          <button type="button" onClick={onClose}
            className="p-1.5 rounded-md text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
            <span>Increase by %</span>
            <input type="number" step="any" value={pct} onChange={(e) => setPct(e.target.value)}
              className="w-28 text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 px-2.5 py-2 outline-none focus:ring-2 focus:ring-slate-400" />
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
            <span>From</span>
            <select value={from} onChange={(e) => setFrom(e.target.value)}
              className="text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 px-2.5 py-2 outline-none focus:ring-2 focus:ring-slate-400">
              {PERIODS.slice(1).map((p) => (
                <option key={p} value={p}>{periodLabel(p, fyStartMonth)}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="px-5 pb-1">
          <div className="flex items-center gap-2 mb-1.5">
            <p className="m-0 text-xs font-semibold text-slate-600 dark:text-slate-300">
              Apply to <span className="font-normal text-slate-400">({picked.size} of {lines.length})</span>
            </p>
            <button type="button" onClick={all}
              className="ml-auto text-[11px] text-blue-600 dark:text-blue-400 hover:underline">All</button>
            <button type="button" onClick={none}
              className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline">None</button>
          </div>
          <div className="max-h-56 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700">
            {lines.map((l) => (
              <label key={l.expense_site_id}
                className="flex items-center gap-2 px-2.5 py-1.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer">
                <input type="checkbox" className="accent-emerald-600"
                  checked={picked.has(l.expense_site_id)}
                  onChange={() => toggle(l.expense_site_id)} />
                <span className="truncate">{l.line_name}</span>
                <span className="ml-auto shrink-0 text-[11px] text-slate-400">{l.account_code || '—'}</span>
              </label>
            ))}
          </div>
        </div>

        <p className="px-5 pt-2 m-0 text-[11px] text-slate-400">
          Steps the rate up from the chosen month onwards. The changed months turn blue in the grid.
          Nothing is written until you Save.
        </p>

        <div className="p-5 flex items-center justify-between gap-3">
          <p className="m-0 text-xs text-red-600 dark:text-red-400">{problem}</p>
          <div className="flex gap-2 shrink-0">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700">
              Cancel
            </button>
            <button type="button" disabled={!!problem} onClick={() => onApply(pctNum, fromNum, picked)}
              className={`px-4 py-2 text-sm font-semibold rounded-md text-white ${problem ? 'bg-slate-400 dark:bg-slate-600' : 'bg-emerald-600 hover:bg-emerald-700'}`}>
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- Page --------------------------------------------------------------------
const VariableRunningCosts = () => {
  const { selectedVersion, selectedSite, fyStartMonth } = useAppContext();

  const [lines, setLines] = useState([]);
  const [rates, setRates] = useState({});          // { expense_site_id: { period: rate } }
  const [catalogue, setCatalogue] = useState([]);
  const [driverValues, setDriverValues] = useState({}); // { driver_code: { period: qty } }
  const [drivers, setDrivers] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [accounts, setAccounts] = useState([]);

  const [dirtyLines, setDirtyLines] = useState({});
  const [dirtyRates, setDirtyRates] = useState({});
  const [picker, setPicker] = useState(null);      // { id, field: 'driver'|'cc', x, y }
  const [addCode, setAddCode] = useState('');
  const [escalateOpen, setEscalateOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState(null);

  const ready = !!selectedVersion;
  const canEdit = ready && !!selectedSite;

  const driverOf = useCallback((l) => l.driver_code || l.default_driver_code || '', []);
  const ccOf = useCallback((l) => l.cost_center_code || l.default_cost_center_code || '', []);
  const driverMeta = useCallback((code) => drivers.find((d) => d.code === code) || null, [drivers]);

  const load = useCallback(async () => {
    if (!ready) { setLines([]); setRates({}); return; }
    setIsLoading(true); setError(null);
    try {
      const res = await distributionService.getVariableCosts();
      if (!res.success) throw new Error(res.message);
      const data = res.data;
      setLines(data.lines || []);

      const byLine = {};
      for (const l of data.lines || []) {
        byLine[l.expense_site_id] = Object.fromEntries(
          PERIODS.map((p) => [p, l.period_data?.[p]?.rate_amount ?? 0])
        );
      }
      setRates(byLine);

      const byDriver = {};
      for (const v of data.driverValues || []) {
        (byDriver[v.driver_code] ||= {})[v.financial_period] = v.driver_qty;
      }
      setDriverValues(byDriver);

      setCatalogue(data.catalogue || []);
      setDrivers(data.drivers || []);
      setCostCenters(data.costCenters || []);
      setAccounts(data.accounts || []);
      setDirtyLines({}); setDirtyRates({});
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load variable running costs.');
      setLines([]);
    } finally { setIsLoading(false); }
  }, [ready]);

  useEffect(() => { load(); }, [load, selectedSite]);

  const linesRef = useRef([]);
  linesRef.current = lines;

  // ---- Derived numbers ----------------------------------------------------------
  const rateAt = useCallback((id, p) => rates[id]?.[p] ?? 0, [rates]);
  const qtyAt = useCallback((driverCode, p) => driverValues[driverCode]?.[p] ?? 0, [driverValues]);

  // The one place the formula lives on the client. It mirrors
  // vw_bud_variable_expense_cost so an unsaved edit previews exactly what the
  // server will store.
  const costAt = useCallback((l, p) => {
    const code = driverOf(l);
    const meta = driverMeta(code);
    const rate = rateAt(l.expense_site_id, p);
    const qty = qtyAt(code, p);
    return meta?.calc_method === 'PCT_OF_VALUE' ? (rate / 100) * qty : rate * qty;
  }, [driverOf, driverMeta, rateAt, qtyAt]);

  const annualOf = useCallback((l) => PERIODS.reduce((a, p) => a + costAt(l, p), 0), [costAt]);

  // A line whose driver has no quantities at this site will silently read zero.
  // Saying so is the difference between a five-minute fix and an afternoon.
  const driverMissing = useCallback((l) => {
    const code = driverOf(l);
    if (!code) return true;
    return !driverValues[code];
  }, [driverOf, driverValues]);

  // The base rate a line is "on": period 1, which the Rate column writes to all
  // twelve. A month differing from it is an intentional exception, and worth
  // marking so it is visible without reading twelve numbers.
  const baseRateOf = useCallback((l) => rateAt(l.expense_site_id, 1), [rateAt]);
  const isOverridden = useCallback(
    (l, p) => p !== 1 && rateAt(l.expense_site_id, p) !== baseRateOf(l),
    [rateAt, baseRateOf]
  );

  const grandTotal = useMemo(
    () => lines.reduce((a, l) => a + PERIODS.reduce((b, p) => b + costAt(l, p), 0), 0),
    [lines, costAt]
  );

  const allSites = !selectedSite;
  const drill = usePeriodDrilldown(PERIODS);

  const gcols = useMemo(() => ([
    ...(allSites ? [{ id: 'site', title: 'Site', width: 120 }] : []),
    { id: 'item', title: 'Expense Item', width: 220 },
    { id: 'gl', title: 'GL Acct', width: 90 },
    { id: 'cc', title: 'Cost Centre', width: 120 },
    { id: 'driver', title: 'Cost Driver', width: 175 },
    { id: 'rate', title: 'Rate', width: 100 },
    { id: 'per', title: 'Per', width: 74 },
    // Periods drill down — Full Year → quarters → months, same as OPEX entry.
    // These months are RATES, so a group column shows the MEAN, not a sum: the
    // sum of twelve tariffs is a number that exists nowhere.
    ...periodColumns(drill, fyStartMonth, { monthWidth: 86, groupWidth: 120, agg: 'avg' }),
    { id: 'annual', title: 'Annual Cost', width: 130 },
    ...(canEdit ? [{ id: 'del', title: '', width: 36 }] : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [allSites, fyStartMonth, canEdit, drill.visibleCols, drill.yearExpanded, drill.expandedQuarters]);
  const colIds = useMemo(() => gcols.map((c) => c.id), [gcols]);

  const rowCount = lines.length + (lines.length ? 1 : 0); // + TOTAL

  const getCellContent = useCallback(([col, row]) => {
    const id = colIds[col];

    // TOTAL row. Month columns stay blank — see the header note: a column of
    // rates in different units has no sum.
    if (row === linesRef.current.length) {
      if (id === 'item') return gcell.text('TOTAL', { readonly: true, theme: T_TOTAL });
      if (id === 'annual') return gcell.number(grandTotal, { readonly: true, display: fmt(grandTotal), theme: T_TOTAL });
      return gcell.text('', { readonly: true, theme: T_TOTAL });
    }

    const l = linesRef.current[row];
    if (!l) return gcell.text('', { readonly: true });

    const code = driverOf(l);
    const meta = driverMeta(code);

    switch (id) {
      case 'site': return gcell.text(l.site_code, { readonly: true });
      case 'item':
        return gcell.text(l.line_name, { readonly: true });
      case 'gl':
        // Fixed on the catalogue — the same expense posts to the same account
        // everywhere, so it is shown here but changed in Budget Setup.
        return gcell.text(l.account_code || '—', { readonly: true, theme: T_INHERITED });
      case 'cc':
        return gcell.text(ccOf(l) || '—', { readonly: true, theme: l.cost_center_code ? undefined : T_INHERITED });
      case 'driver': {
        if (!code) return gcell.text('— none set —', { readonly: true, theme: T_WARN });
        const name = meta?.description || code;
        return gcell.text(driverMissing(l) ? `${name} (not on site)` : name, {
          readonly: true,
          theme: driverMissing(l) ? T_WARN : (l.driver_code ? undefined : T_INHERITED),
        });
      }
      case 'rate': {
        // Always editable, and always writes all twelve. Once months have been
        // taken off the base rate a single figure would misrepresent the line,
        // so it shows the spread — but typing over it still resets every month,
        // which is the quickest way back to a flat year.
        const vals = PERIODS.map((p) => rateAt(l.expense_site_id, p));
        const lo = Math.min(...vals); const hi = Math.max(...vals);
        return gcell.number(vals[0], {
          readonly: !canEdit,
          display: lo === hi ? fmtRate(lo) : `${fmtRate(lo)}–${fmtRate(hi)}`,
        });
      }
      case 'per':
        return gcell.text(meta?.calc_method === 'PCT_OF_VALUE' ? '% of value' : (meta?.driver_uom || '—'),
          { readonly: true, theme: T_INHERITED });
      case 'annual': {
        const a = annualOf(l);
        return gcell.number(a, { readonly: true, display: fmt(a), theme: T_AGG });
      }
      case 'del': return gcell.text('✕', { readonly: true, theme: { textDark: '#94a3b8' } });
      default: {
        const ps = periodsOfCol(gcols, id);
        if (!ps) return gcell.text('', { readonly: true });
        // A single month IS the rate: editable, and highlighted once it differs
        // from the base — that is the January tariff step, made visible. A
        // quarter or the year is the MEAN rate over those months, read-only.
        const v = aggPeriods(ps, (p) => rateAt(l.expense_site_id, p), 'avg');
        if (ps.length > 1) {
          return gcell.number(v, { readonly: true, display: fmtRate(v), theme: T_AGG });
        }
        return gcell.number(v, {
          readonly: !canEdit,
          display: fmtRate(v),
          theme: isOverridden(l, ps[0]) ? T_OVERRIDDEN : undefined,
        });
      }
    }
  }, [colIds, gcols, canEdit, grandTotal, driverOf, driverMeta, ccOf,
      driverMissing, rateAt, isOverridden, annualOf]);

  // ---- Edits ---------------------------------------------------------------------
  const setRate = useCallback((id, periods, value) => {
    setRates((prev) => {
      const next = { ...(prev[id] || {}) };
      for (const p of periods) next[p] = value;
      return { ...prev, [id]: next };
    });
    setDirtyRates((prev) => {
      const next = { ...prev };
      for (const p of periods) next[`${id}:${p}`] = true;
      return next;
    });
  }, []);

  const patchLine = useCallback((id, fields) => {
    setLines((prev) => prev.map((l) => (l.expense_site_id === id ? { ...l, ...fields } : l)));
    setDirtyLines((prev) => ({ ...prev, [id]: true }));
  }, []);

  const onCellEdited = useCallback(([col, row], newValue) => {
    if (!canEdit) return;
    const id = colIds[col];
    const l = linesRef.current[row];
    if (!l) return;
    const n = Number(newValue.data);
    if (!Number.isFinite(n)) return;

    // Rate fills the year; a month overrides just itself. Between them that is
    // the whole interaction — set it once, then fix the month that differs.
    if (id === 'rate') { setRate(l.expense_site_id, PERIODS, n); return; }
    const ps = periodsOfCol(gcols, id);
    // Months only. A quarter column is the mean of three rates — writing it
    // back would silently flatten months somebody deliberately varied.
    if (ps?.length === 1) setRate(l.expense_site_id, ps, n);
  }, [colIds, gcols, canEdit, setRate]);

  const removeLine = useCallback(async (l) => {
    if (!window.confirm(`Remove "${l.line_name}" from this site? Its rates go with it. The line stays in the catalogue for other sites.`)) return;
    setError(null);
    try {
      await distributionService.deleteVariableCostLine(l.expense_site_id);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to remove this line.');
    }
  }, [load]);

  const onCellClicked = useCallback(([col, row], event) => {
    if (!canEdit) return;
    const id = colIds[col];
    const l = linesRef.current[row];
    if (!l) return;
    const b = event?.bounds;
    const pos = { x: b ? b.x : 200, y: b ? b.y + b.height : 200 };
    if (id === 'driver') setPicker({ id: l.expense_site_id, field: 'driver', ...pos });
    else if (id === 'cc') setPicker({ id: l.expense_site_id, field: 'cc', ...pos });
    else if (id === 'del') removeLine(l);
  }, [colIds, canEdit, removeLine]);

  const applyPick = useCallback((id, field, code) => {
    patchLine(id, field === 'driver' ? { driver_code: code || '' } : { cost_center_code: code || '' });
    setPicker(null);
  }, [patchLine]);

  // ---- Add / escalate / save -------------------------------------------------------
  const addOptions = useMemo(() => {
    const taken = new Set(lines.filter((l) => l.site_code === selectedSite).map((l) => l.line_code));
    return catalogue.filter((c) => !taken.has(c.line_code))
      .map((c) => ({ code: c.line_code, name: c.line_name }));
  }, [catalogue, lines, selectedSite]);

  const addLine = async () => {
    if (!addCode || isAdding) return;
    setIsAdding(true); setError(null);
    try {
      const res = await distributionService.addVariableCostLine(addCode);
      if (!res.success) throw new Error(res.message);
      setAddCode('');
      await load();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to add this line.');
    } finally { setIsAdding(false); }
  };

  const applyEscalation = (pct, fromPeriod, picked) => {
    const mult = 1 + pct / 100;
    for (const l of linesRef.current) {
      if (!picked.has(l.expense_site_id)) continue;
      const base = rates[l.expense_site_id] || {};
      const after = PERIODS.filter((p) => p >= fromPeriod);
      setRates((prev) => {
        const next = { ...(prev[l.expense_site_id] || {}) };
        for (const p of after) next[p] = (base[p] ?? 0) * mult;
        return { ...prev, [l.expense_site_id]: next };
      });
      setDirtyRates((prev) => {
        const next = { ...prev };
        for (const p of after) next[`${l.expense_site_id}:${p}`] = true;
        return next;
      });
    }
    setEscalateOpen(false);
  };

  const hasChanges = Object.keys(dirtyLines).length > 0 || Object.keys(dirtyRates).length > 0;

  const save = useCallback(async () => {
    if (!hasChanges || isSaving) return;
    setIsSaving(true); setError(null);
    try {
      // rate_mode is no longer a mode the user picks — the page has one row
      // either way. It is derived here purely so the stored flag stays true to
      // the data for anything that reads it later: flat year = ANNUAL.
      const modeOf = (id) => {
        const vals = PERIODS.map((p) => rates[id]?.[p] ?? 0);
        return vals.every((v) => v === vals[0]) ? 'ANNUAL' : 'MONTHLY';
      };
      // A rate edit changes what the flag should say, so lines touched only via
      // their rates have to be written too.
      const touched = new Set([
        ...Object.keys(dirtyLines),
        ...Object.keys(dirtyRates).map((k) => k.split(':')[0]),
      ]);
      const payloadLines = linesRef.current
        .filter((l) => touched.has(l.expense_site_id))
        .map((l) => ({
          expense_site_id: l.expense_site_id,
          driver_code: l.driver_code,
          cost_center_code: l.cost_center_code,
          rate_mode: modeOf(l.expense_site_id),
          is_active: l.is_active,
        }));
      const payloadRates = Object.keys(dirtyRates).map((k) => {
        const [expense_site_id, p] = k.split(':');
        return { expense_site_id, financial_period: Number(p), rate_amount: rates[expense_site_id]?.[p] ?? 0 };
      });
      const res = await distributionService.saveVariableCosts({ lines: payloadLines, rates: payloadRates });
      if (!res.success) throw new Error(res.message);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save variable running costs.');
    } finally { setIsSaving(false); }
  }, [hasChanges, isSaving, dirtyLines, dirtyRates, rates, load]);

  const toolbar = useMemo(() => (
    <>
      <button type="button" onClick={drill.yearExpanded ? drill.collapseAll : drill.expandAll}
        className="px-3 py-1 rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800">
        {drill.yearExpanded ? 'Collapse periods' : 'Expand periods'}
      </button>
      <button type="button" onClick={() => setEscalateOpen(true)} disabled={lines.length === 0}
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-40">
        <TrendingUp className="w-3.5 h-3.5" /> Escalate…
      </button>
      <button type="button" onClick={save} disabled={isSaving || !hasChanges}
        className={`px-3 py-1 rounded-md border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
          hasChanges
            ? 'border-emerald-700 bg-emerald-600 text-white font-semibold hover:bg-emerald-700'
            : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
        {isSaving ? 'Saving…' : 'Save'}
      </button>
    </>
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [isSaving, hasChanges, save, lines.length, drill.yearExpanded, drill.collapseAll, drill.expandAll]);

  const anyMissing = lines.some(driverMissing);

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-6 pb-4">
        <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          Warehousing · Variable Running Costs
        </h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Rate × driver, per period. Driver quantities come from Operational Drivers; the lines themselves are
          defined in Budget Setup → Variable Expenses. Enter the rate here.
        </p>
        {error && <div className="mt-3 p-2.5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md">{error}</div>}
        {anyMissing && (
          <div className="mt-3 p-2.5 rounded-md border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 text-sm text-amber-800 dark:text-amber-300">
            Some lines point at a driver this site has not set up — those months compute to zero.
            Add the driver on Warehousing → Operational Drivers, or point the line at one that exists.
          </div>
        )}
      </div>

      {ready && canEdit && (
        <div className="shrink-0 px-6 pb-3">
          <div className="flex flex-wrap items-end gap-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3">
            <div className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
              <span>Add an expense line</span>
              <SearchSelect className="w-80" value={addCode} onChange={setAddCode} options={addOptions}
                placeholder="Select from the catalogue…"
                emptyText="Every catalogue line is already on this site." />
            </div>
            <button type="button" onClick={addLine} disabled={!addCode || isAdding}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md text-white mb-0.5 ${
                !addCode || isAdding ? 'bg-slate-400 dark:bg-slate-600 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700'}`}>
              {isAdding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
            </button>
            <p className="m-0 ml-auto text-[11px] text-slate-400 max-w-sm">
              New expense lines — and their GL account — are created in Budget Setup → Variable Expenses.
            </p>
          </div>
        </div>
      )}

      {/* This page scrolls, because the posting summary sits below the grid.
          So the grid wrapper gets `flex flex-col` — which MantleDataGrid's
          fitHeight contract needs to measure itself — but deliberately NOT
          `max-h-full`: capping it to the viewport would give the grid its own
          inner scrollbar competing with the page's. Natural height, page
          scrolls. */}
      <div className="flex-1 min-h-0 px-6 pb-4 overflow-y-auto space-y-4">
        <div className="flex flex-col rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900">
          {!ready ? (
            <div className="p-10 text-center text-sm text-slate-400">Select a budget version in the top bar to begin.</div>
          ) : isLoading ? (
            <div className="p-4 text-xs text-slate-400">Loading…</div>
          ) : lines.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">
              {canEdit
                ? 'No variable expense lines on this site yet — add one above.'
                : 'No lines captured. Select a site in the top bar to add them.'}
            </div>
          ) : (
            <MantleDataGrid
              gridId="dist-variable-running"
              fitHeight
              fitHeightPadding={12}
              columns={gcols}
              rowCount={rowCount}
              getCellContent={getCellContent}
              onCellEdited={onCellEdited}
              onCellClicked={onCellClicked}
              onHeaderClicked={(col) => periodHeaderToggle(drill, colIds[col])}
              freezeColumns={allSites ? 2 : 1}
              hint={canEdit
                ? 'Click Full Year → quarters → months to drill into the periods; a group column is the AVERAGE rate over those months, because rates do not add up. Type in Rate to fill the year, then overtype any month that differs — changed months turn blue. Cost per month is in the posting summary below.'
                : 'Click Full Year → quarters → months to drill into the periods. All Sites — read-only; select one site in the top bar to edit.'}
              toolbar={toolbar}
            />
          )}
        </div>

        {lines.length > 0 && (
          <PostingSummary
            storageKey="dist-variable-running"
            entries={lines.map((l) => ({
              site: l.site_code,
              gl: l.account_code,
              glName: accounts.find((a) => a.code === l.account_code)?.description || '',
              costCentre: ccOf(l),
              values: PERIODS.map((p) => costAt(l, p)),
            }))}
          />
        )}
      </div>

      {picker && (
        <PickerPopover
          key={`${picker.id}-${picker.field}`}
          anchor={picker}
          options={picker.field === 'driver'
            ? drivers.map((d) => ({ code: d.code, label: d.description, hint: d.driver_uom }))
            : costCenters}
          value={picker.field === 'driver'
            ? lines.find((l) => l.expense_site_id === picker.id)?.driver_code
            : lines.find((l) => l.expense_site_id === picker.id)?.cost_center_code}
          onPick={(code) => applyPick(picker.id, picker.field, code)}
          onClear={() => applyPick(picker.id, picker.field, '')}
          clearLabel="Use the catalogue default"
          onClose={() => setPicker(null)}
        />
      )}

      {escalateOpen && (
        <EscalateModal
          fyStartMonth={fyStartMonth}
          lines={lines}
          onApply={applyEscalation}
          onClose={() => setEscalateOpen(false)}
        />
      )}
    </div>
  );
};

export default VariableRunningCosts;
